import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { cva } from "class-variance-authority";
import { Info, AlertTriangle, CircleX, CheckCircle, Lightbulb } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import type { CalloutBlock as CalloutBlockType } from "@/canvas/types/blocks";
import { useCanvasStore } from "../../store";
import { cn } from "@/lib/utils";

// CVA variants for callout styling (per RESEARCH.md pattern)
const calloutVariants = cva("relative w-full rounded-lg border p-4", {
  variants: {
    variant: {
      info: "bg-blue-50 border-blue-200 text-blue-900",
      warning: "bg-amber-50 border-amber-200 text-amber-900",
      error: "bg-red-50 border-red-200 text-red-900",
      success: "bg-green-50 border-green-200 text-green-900",
      tip: "bg-purple-50 border-purple-200 text-purple-900",
    },
  },
  defaultVariants: { variant: "info" },
});

// Icon styles per variant
const iconVariants = cva("h-5 w-5 shrink-0", {
  variants: {
    variant: {
      info: "text-blue-600",
      warning: "text-amber-600",
      error: "text-red-600",
      success: "text-green-600",
      tip: "text-purple-600",
    },
  },
  defaultVariants: { variant: "info" },
});

// Variant icons mapping
const variantIcons = {
  info: Info,
  warning: AlertTriangle,
  error: CircleX,
  success: CheckCircle,
  tip: Lightbulb,
} as const;

type CalloutVariant = CalloutBlockType["content"]["variant"];

interface CalloutBlockProps {
  block: CalloutBlockType;
  readOnly?: boolean;
}

export function CalloutBlock({ block, readOnly = false }: CalloutBlockProps) {
  const { t } = useTranslation();
  const updateBlockContent = useCanvasStore((state) => state.updateBlockContent);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingText, setIsEditingText] = useState(false);

  const { variant, title, text } = block.content;
  const Icon = variantIcons[variant];

  // Note: V1 store's updateBlockContent expects BlockContent which doesn't include
  // callout type. Cast to any during V1->V2 transition (store update in Phase 27-03).
  const handleVariantChange = useCallback(
    (newVariant: CalloutVariant) => {
      updateBlockContent(block.id, {
        ...block.content,
        variant: newVariant,
      } as unknown as Parameters<typeof updateBlockContent>[1]);
    },
    [block.id, block.content, updateBlockContent],
  );

  const handleTitleChange = useCallback(
    (newTitle: string) => {
      updateBlockContent(block.id, {
        ...block.content,
        title: newTitle || undefined, // Remove title if empty
      } as unknown as Parameters<typeof updateBlockContent>[1]);
    },
    [block.id, block.content, updateBlockContent],
  );

  const handleTextChange = useCallback(
    (newText: string) => {
      updateBlockContent(block.id, {
        ...block.content,
        text: newText,
      } as unknown as Parameters<typeof updateBlockContent>[1]);
    },
    [block.id, block.content, updateBlockContent],
  );

  const variantLabels: Record<CalloutVariant, string> = {
    info: t("canvas.blocks.callout.variants.info", "Info"),
    warning: t("canvas.blocks.callout.variants.warning", "Warning"),
    error: t("canvas.blocks.callout.variants.error", "Error"),
    success: t("canvas.blocks.callout.variants.success", "Success"),
    tip: t("canvas.blocks.callout.variants.tip", "Tip"),
  };

  return (
    <div className={calloutVariants({ variant })}>
      <div className="flex gap-3">
        {/* Icon with variant dropdown - per CONTEXT.md: clicking icon reveals dropdown */}
        {readOnly ? (
          <div className={cn(iconVariants({ variant }), "mt-0.5")}>
            <Icon className="h-5 w-5" />
          </div>
        ) : (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={cn(
                  iconVariants({ variant }),
                  "cursor-pointer hover:opacity-70 transition-opacity mt-0.5",
                )}
                aria-label={t("canvas.blocks.callout.changeVariant", "Change callout type")}
              >
                <Icon className="h-5 w-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {(Object.keys(variantIcons) as CalloutVariant[]).map((v) => {
                const VIcon = variantIcons[v];
                return (
                  <DropdownMenuItem
                    key={v}
                    onClick={() => handleVariantChange(v)}
                    className={cn(v === variant && "bg-accent")}
                  >
                    <VIcon className={cn("h-4 w-4 mr-2", iconVariants({ variant: v }))} />
                    {variantLabels[v]}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        {/* Content area */}
        <div className="flex-1 min-w-0 space-y-2">
          {/* Title (optional) */}
          {isEditingTitle && !readOnly ? (
            <Input
              value={title || ""}
              onChange={(e) => handleTitleChange(e.target.value)}
              onBlur={() => setIsEditingTitle(false)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === "Escape") {
                  setIsEditingTitle(false);
                }
              }}
              placeholder={t("canvas.blocks.callout.titlePlaceholder", "Add a title...")}
              className="font-semibold bg-transparent border-0 border-b border-current/20 rounded-none px-0 h-auto py-1 focus-visible:ring-0 focus-visible:border-current/40"
              autoFocus
            />
          ) : (
            <div
              onClick={() => !readOnly && setIsEditingTitle(true)}
              className={cn(
                "font-semibold min-h-[24px]",
                !readOnly && "cursor-text",
                !title && "text-current/50 italic",
              )}
            >
              {title ||
                (readOnly ? "" : t("canvas.blocks.callout.titlePlaceholder", "Add a title..."))}
            </div>
          )}

          {/* Text content */}
          {isEditingText && !readOnly ? (
            <Textarea
              value={text}
              onChange={(e) => handleTextChange(e.target.value)}
              onBlur={() => setIsEditingText(false)}
              placeholder={t("canvas.blocks.callout.textPlaceholder", "Add content...")}
              className="bg-transparent border-0 rounded-none px-0 py-1 resize-none focus-visible:ring-0 min-h-[60px]"
              autoFocus
            />
          ) : (
            <div
              onClick={() => !readOnly && setIsEditingText(true)}
              className={cn(
                "whitespace-pre-wrap min-h-[24px]",
                !readOnly && "cursor-text",
                !text && "text-current/50 italic",
              )}
            >
              {text ||
                (readOnly ? "" : t("canvas.blocks.callout.textPlaceholder", "Add content..."))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
